import { useEffect } from "react";
import { useParams } from "react-router-dom";
import LazyLoad from "vanilla-lazyload";
import { ProgramDetail } from "src/store";
import { useSelector } from "react-redux";
import { DateService, StringService, _ } from "src/services";
import { Event, QualityLock, QualityUnlock, Time } from "src/components/svg";
import { PlayerActions } from "src/components/player";
import phSingleItem from "src/assets/static/img/main/icons/ph_single_item.png";
import phTeamLogo from "src/assets/static/img/main/icons/ph_team_logo.png";

export const ProgramDetailCard: React.FC = () => {
  //#region depenencies

  const { id }: any = useParams();
  const programInfo: ProgramDetail = useSelector(
    (state: any) => state.ProgramViewState
  );

  //#endregion

  //#region  effects

  useEffect(() => {
    const updateImages = () => {
      const lazyload = new LazyLoad({
        elements_selector: ".lazy.dt" + id,
        callback_error: (element: HTMLElement) => {
          const placeholder = element.getAttribute("data-placeholder");
          if (placeholder) element.setAttribute("src", placeholder);
        },
      });

      lazyload.update();
    };

    if (programInfo.info) updateImages();
  }, [id, programInfo.info]);

  //#endregion

  if (programInfo.loading)
    return (
      <div className="container">
        <div className="row">
          <div className="col-12 col-lg-8 col-md-12 offset-lg-2 p-0 mb-3">
            <div className="program-details pb-3 exists">
              <div className="card m-0 w-100">
                <div className={`layout-1-sides program-card text-center`}>
                  <div className="card-body py-3 shimmer" />
                </div>
                <div
                  className="row d-flex align-items-center justify-content-center program-qualities rtl shimmer"
                  style={{ height: 29 }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );

  if (!programInfo.info) return null;

  const {
    layout,
    thumbnail,
    title,
    upperTitle,
    layoutData,
    description,
    startAt,
    streams,
    secDesc,
    iframe,
  } = programInfo.info;
  const { subs } = programInfo.currentStream || streams[0] || {};

  //#region helpers

  const renderThumbnail = () => {
    if (layout === 2 && layoutData) return null;
    return (
      <div className="layout-one-thumb text-left d-inline-block">
        <img
          data-placeholder={phSingleItem}
          src={phSingleItem}
          key={`thumb${id}`}
          data-src={thumbnail}
          alt={title}
          className={`lazy dt${id}`}
        />
      </div>
    );
  };

  const renderGuestLogo = () => {
    if (layout === 1 || !layoutData) return null;
    return (
      <div className="col-md-3 col-3 d-flex justify-content-start align-items-center p-0 px-md-2">
        <div className="d-flex bg-logo justify-content-center align-items-center ml-1 ml-md-2">
          <img
            alt={layoutData.guestName}
            key={`guest${id}`}
            data-src={layoutData.guestLogo}
            data-placeholder={phTeamLogo}
            src={phTeamLogo}
            className={`logo lazy dt${id}`}
          />
        </div>
      </div>
    );
  };

  const renderHostLogos = () => {
    if (layout === 1 || !layoutData) return null;
    return (
      <div className="col-md-3 col-3 d-flex justify-content-end align-items-center p-0 px-md-2">
        <div className="d-flex bg-logo justify-content-center align-items-center">
          <img
            alt={layoutData.hostName}
            key={`host${id}`}
            data-src={layoutData.hostLogo}
            data-placeholder={phTeamLogo}
            src={phTeamLogo}
            className={`logo lazy dt${id}`}
          />
        </div>
      </div>
    );
  };

  const renderTitle = () => {
    return (
      <div
        className={`${
          layout === 1 || !layoutData
            ? "col-md-9 col-9"
            : "col-md-6 col-6  justify-content-center"
        } d-flex match-content flex-column match-content`}
      >
        <div className="pr-2 pr-md-3 text-rtl">
          <h1
            className={`match-title${
              layout === 1 || !layoutData ? " text-align-right text-right" : ""
            }`}
          >
            {title}
          </h1>
          <span
            className={`btn-sport-name${
              layout === 1 || !layoutData ? " text-right float-right" : ""
            }`}
          >
            {upperTitle}
          </span>
        </div>
      </div>
    );
  };

  const renderDetail = () => {
    return (
      <div className="container p-sm-0">
        {description && (
          <div className="program-description text-rtl text-right mt-3">
            <div
              className="desc"
              dangerouslySetInnerHTML={{
                __html: StringService.toHtml(description),
              }}
            />
          </div>
        )}
        <table className="program-details-table-mobile w-100 text-rtl text-right my-3 d-lg-none">
          <tbody>
            <tr>
              <th>
                <span className="ml-2">
                  <Event />
                </span>
                <span> {_("broadcastDate")}</span>
              </th>
              <td className="text-left">
                {DateService.persianDate(startAt, "dddd YYYY/M/D")}
              </td>
            </tr>
            <tr>
              <th>
                <span className="ml-2">
                  <Time />
                </span>
                <span> {_("broadcastTime")}</span>
              </th>
              <td className="text-left">
                {DateService.persianDate(startAt, "HH:mm")}
              </td>
            </tr>
          </tbody>
        </table>
        <div className="d-flex d-lg-none align-items-center pr-2">
          <PlayerActions />
        </div>
      </div>
    );
  };

  //#endregion

  return (
    <div className="container">
      <div className="row">
        <div className="col-12 col-lg-8 col-md-12 offset-lg-2 p-0 mb-3">
          <div className="program-details pb-3 exists">
            <div className="card m-0 w-100">
              <div
                className={`layout-${layout}-sides program-card text-center`}
              >
                {renderThumbnail()}
                <div className="card-body py-3">
                  <div className="row no-gutters">
                    {layout === 1 && <div className="col-md-3 col-3"></div>}
                    {renderGuestLogo()}
                    {renderTitle()}
                    {renderHostLogos()}
                  </div>
                </div>
              </div>
              {!iframe && subs && subs.length && (
                <div className="row d-flex align-items-center justify-content-center program-qualities rtl">
                  {secDesc && (
                    <div
                      className="sec-desc"
                      dangerouslySetInnerHTML={{
                        __html: StringService.toHtml(secDesc),
                      }}
                    />
                  )}
                  <span>{_("qualities")}:</span>
                  {subs.map(({ lock, resolution }, i) => {
                    const idx = resolution.indexOf("x") || -1;
                    const res = resolution.substring(idx + 1) + "p";
                    return (
                      <span key={i} className={lock ? "locked" : "unlocked"}>
                        {res}
                        {lock ? <QualityLock /> : <QualityUnlock />}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
            {renderDetail()}
          </div>
        </div>
      </div>
    </div>
  );
};
